import { useEffect, useMemo, useState, type JSX } from 'react';
import { getCollection, type CollectionEntry, type TsukumogamiRarity } from '../../utils/collection';
import { syncZukanWithServer } from '../../utils/collectionSync';
import {
  ZUKAN_MANIFEST,
  getZukanManifestByKey,
  getZukanManifestForScenario,
  type ZukanManifestEntry
} from '../../collection/zukanManifest';
import { RARITY_GLYPH, endingGlyph } from '../../utils/endingGlyph';
import { resolveMediaRefToUrl } from '../../utils/mediaRef';
import './tsukumogami-collection.css';

/**
 * 没入 P5「声を取り戻した付喪神図鑑」: 端末に貯まったコレクションのモーダル表示。
 * P9-1「欠けの可視化」: 収集対象の全体集合（zukanManifest）を描画し、未収集は
 * シルエット+「？？？」+一文ヒントのスロットとして見せる（コンプ欲の発火点）。
 *
 * 設計意図:
 * - エンディング画面・スタート画面の双方から開ける（シナリオ未プレイでも欠けを確認できる）。
 * - マニフェストは公開済シナリオ分のみ（§10 論点④）。未収集スロットは名前・説明を出さない。
 * - 回収した結末（endingTypes）は世界内の一字表記で示す。結末総数はマニフェストが
 *   declaredEndingCount を宣言する場合のみ「n / m」を出す（捏造しない）。
 * - 世界観 §7.4: 文言は九十九商会トーン・常体。開発用語・内部 ID は一切出さない
 *   （endingType の生値も画面に出さず、未知の値は「？」に倒す）。
 * - prefers-reduced-motion はアニメーション側（CSS）で尊重。
 * - 意匠（slate/amber・rarity バッジ）は P8-2 で骨董化予定のため、今回は色・レイアウトの
 *   刷新をせずスロット追加と表示情報の追加のみ。
 */

export interface TsukumogamiCollectionProps {
  /** モーダルを閉じる。 */
  onClose: () => void;
  /** 指定シナリオの図鑑枠がある場合、別作品・旧版記録を表示しない。 */
  scenarioId?: string;
}

// P8-3: 結末・rarity の一字表記は utils/endingGlyph.ts（SSoT）を消費する。
// 未知の endingType は生値を出さず「？」に倒す（内部値の非漏出）。
function endingChipGlyph(type: string): string {
  return endingGlyph(type) ?? '？';
}

function rarityClass(rarity?: TsukumogamiRarity): string {
  return rarity ? `tsukumo-card--${rarity}` : 'tsukumo-card--common';
}

/** クリア日時を「YYYY.MM.DD」で表す（端末ローカル）。 */
function formatClearedAt(ms: number): string {
  try {
    const d = new Date(ms);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}.${m}.${day}`;
  } catch {
    return '';
  }
}

function TsukumogamiCard({
  entry,
  declaredEndingCount
}: {
  entry: CollectionEntry;
  /** マニフェストが宣言する結末総数（あれば「n / m」表示に使う）。 */
  declaredEndingCount?: number;
}): JSX.Element {
  const imageUrl = entry.illustrationAssetId ? resolveMediaRefToUrl(entry.illustrationAssetId) : null;
  const initial = entry.name.trim().charAt(0) || '?';
  const endingTypes = entry.endingTypes;
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const canExpandDescription = Boolean(entry.description && entry.description.length > 48);

  return (
    <div className={`tsukumo-card ${rarityClass(entry.rarity)}`}>
      <div className="tsukumo-card__art">
        {imageUrl ? (
          <img className="tsukumo-card__img" src={imageUrl} alt="" />
        ) : (
          // illustrationAssetId が無い / 解決できない場合は名前頭文字のプレースホルダ。
          <div className="tsukumo-card__placeholder" aria-hidden="true">
            <span className="tsukumo-card__initial">{initial}</span>
          </div>
        )}
        {entry.rarity && (
          <span className="tsukumo-card__rarity" title={entry.rarity}>
            {RARITY_GLYPH[entry.rarity]}
          </span>
        )}
      </div>

      <div className="tsukumo-card__body">
        <h3 className="tsukumo-card__name">{entry.name}</h3>
        {entry.description && (
          <>
            <p
              className={`tsukumo-card__desc${descriptionExpanded ? ' tsukumo-card__desc--expanded' : ''}`}
            >
              {entry.description}
            </p>
            {canExpandDescription && (
              <button
                type="button"
                className="tsukumo-card__desc-toggle"
                aria-expanded={descriptionExpanded}
                onClick={() => setDescriptionExpanded((value) => !value)}
              >
                {descriptionExpanded ? '閉じる' : '全文を見る'}
              </button>
            )}
          </>
        )}
        {/* P9-1: 回収した結末。総数は declaredEndingCount がある場合のみ分母を出す（捏造しない）。 */}
        {endingTypes.length > 0 && (
          <div className="tsukumo-card__endings">
            <span className="tsukumo-card__endings-label">
              結末 {endingTypes.length}
              {declaredEndingCount ? ` / ${declaredEndingCount}` : ''}
            </span>
            <span className="tsukumo-card__ending-chips">
              {endingTypes.map((type) => (
                <span key={type} className="tsukumo-card__ending-chip">
                  {endingChipGlyph(type)}
                </span>
              ))}
            </span>
          </div>
        )}
        <div className="tsukumo-card__meta">
          <span className="tsukumo-card__cleared">{formatClearedAt(entry.lastClearedAt)}</span>
          {entry.clearCount > 1 && <span className="tsukumo-card__count">{entry.clearCount}周</span>}
        </div>
      </div>
    </div>
  );
}

/** P9-1: 未収集スロット。シルエット+「？？？」+一文ヒントのみ（名前・説明は出さない）。 */
function UnknownTsukumogamiCard({ slot }: { slot: ZukanManifestEntry }): JSX.Element {
  return (
    <div className="tsukumo-card tsukumo-card--unknown">
      <div className="tsukumo-card__art">
        <div className="tsukumo-card__placeholder" aria-hidden="true">
          <span className="tsukumo-card__silhouette" />
        </div>
        {slot.rarity && (
          <span className="tsukumo-card__rarity tsukumo-card__rarity--unknown">
            {RARITY_GLYPH[slot.rarity]}
          </span>
        )}
      </div>
      <div className="tsukumo-card__body">
        <h3 className="tsukumo-card__name tsukumo-card__name--unknown">？？？</h3>
        <p className="tsukumo-card__desc">{slot.hint}</p>
      </div>
    </div>
  );
}

function EmptyLead(): JSX.Element {
  // P9-1: マニフェスト導入後の空状態。？？？スロット（グリッド側）が欠けを見せるので、
  // ここは着地の定型文のみ（従来の汎用シルエット3つはスロットに置き換え）。
  return (
    <div className="tsukumo-empty tsukumo-empty--with-slots">
      <p className="tsukumo-empty__lead">まだ声を取り戻した物はいません。</p>
      <p className="tsukumo-empty__sub">
        影だけが、先に台帳に載っています。身の回りにも、声を持ちたがっている物があるかもしれません。
      </p>
    </div>
  );
}

function EmptyState(): JSX.Element {
  // フォールバック: マニフェストが空のとき（通常到達しない）。従来の空状態表示を維持する。
  return (
    <div className="tsukumo-empty">
      <div className="tsukumo-empty__silhouettes" aria-hidden="true">
        <span className="tsukumo-empty__silhouette" />
        <span className="tsukumo-empty__silhouette" />
        <span className="tsukumo-empty__silhouette" />
      </div>
      <p className="tsukumo-empty__lead">まだ声を取り戻した物はいません。</p>
      <p className="tsukumo-empty__sub">
        身の回りにも、声を持ちたがっている物があるかもしれません。
      </p>
    </div>
  );
}

export function TsukumogamiCollection({ onClose, scenarioId }: TsukumogamiCollectionProps): JSX.Element {
  // P9-2: ローカルキャッシュ（localStorage）を即表示し、裏でサーバ台帳と同期する。
  // サーバへ到達できない場合（Worker 未対応・オフライン）は null が返り、表示はローカルのまま
  // 変わらない（graceful degradation・エラーは UI に出さない）。
  const [collected, setCollected] = useState<CollectionEntry[]>(() => getCollection());
  useEffect(() => {
    let mounted = true;
    void syncZukanWithServer().then((merged) => {
      if (mounted && merged) setCollected(merged);
    });
    return () => {
      mounted = false;
    };
  }, []);

  const { entries, unknownSlots, manifestByKey } = useMemo(() => {
    const visibleManifest = getZukanManifestForScenario(scenarioId);
    const isScenarioScoped = Boolean(
      scenarioId && ZUKAN_MANIFEST.some((entry) => entry.scenarioIds?.includes(scenarioId))
    );
    const visibleKeys = new Set(visibleManifest.map((entry) => entry.key));
    // 最新クリアが手前に来るよう降順（新しく声を取り戻した物が目に入る）。
    const sorted = collected
      .filter((entry) => !isScenarioScoped || visibleKeys.has(entry.key))
      .sort((a, b) => b.lastClearedAt - a.lastClearedAt);
    const collectedKeys = new Set(sorted.map((e) => e.key));
    // マニフェストのうち未収集分だけを？？？スロットにする（収集済みは実カードが出る）。
    const unknown = visibleManifest.filter((m) => !collectedKeys.has(m.key));
    return { entries: sorted, unknownSlots: unknown, manifestByKey: getZukanManifestByKey() };
  }, [collected, scenarioId]);

  // 全体集合 = 収集済み ∪ マニフェスト未収集分（マニフェスト外の収集済みも数に入れる）。
  const totalSlots = entries.length + unknownSlots.length;
  const hasAnySlot = totalSlots > 0;

  // Esc で閉じる（任意要件・キーボード操作の取りこぼし防止）。
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="tsukumo-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="声を取り戻した付喪神図鑑"
      onClick={onClose}
    >
      <div className="tsukumo-modal" onClick={(e) => e.stopPropagation()}>
        <div className="tsukumo-modal__header">
          <div className="tsukumo-modal__titles">
            <h2 className="tsukumo-modal__title">声を取り戻した付喪神</h2>
            <p className="tsukumo-modal__subtitle">
              九十九商会端末に残された記録
              {hasAnySlot && (
                <span className="tsukumo-modal__total">
                  　{entries.length} / {totalSlots}体
                </span>
              )}
            </p>
          </div>
          <button className="tsukumo-modal__close" onClick={onClose} aria-label="閉じる">
            ×
          </button>
        </div>

        <div className="tsukumo-modal__body">
          {!hasAnySlot ? (
            <EmptyState />
          ) : (
            <>
              <div className="tsukumo-grid">
                {entries.map((entry) => (
                  <TsukumogamiCard
                    key={entry.key}
                    entry={{
                      ...entry,
                      description: manifestByKey.get(entry.key)?.description ?? entry.description
                    }}
                    declaredEndingCount={manifestByKey.get(entry.key)?.declaredEndingCount}
                  />
                ))}
                {unknownSlots.map((slot) => (
                  <UnknownTsukumogamiCard key={slot.key} slot={slot} />
                ))}
              </div>
              {entries.length === 0 && <EmptyLead />}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default TsukumogamiCollection;
